import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Award, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Skills({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/skills`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  const levelColor = (level) => {
    if (level === 'Expert') return 'bg-[#1A4331] text-white';
    if (level === 'Strong') return 'bg-[#B4D330]/20 text-[#1A4331]';
    if (level === 'Improving') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
    if (level === 'Beginner') return 'bg-[#8AB4F8]/20 text-[#8AB4F8]';
    return 'bg-[#F0F2EB] text-[#5C665C]';
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Grower Skills</h1>
          <p className="text-base text-[#5C665C] mt-2">Track your growing journey across 7 core skill areas</p>
        </div>

        {/* Overall Score Hero */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#255C44] rounded-3xl p-8 text-white" data-testid="overall-skills-hero">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold tracking-wider uppercase opacity-80">Overall Grower Level</p>
              <h2 className="text-5xl font-bold mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{data?.overall_level}</h2>
              <p className="text-sm opacity-90 mt-2">Keep gardening to improve your skills</p>
            </div>
            <div className="text-right">
              <Award className="w-12 h-12 mb-2 opacity-80" />
              <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{data?.overall_score}%</p>
            </div>
          </div>
        </div>

        {/* Skill Areas */}
        <div className="grid md:grid-cols-2 gap-4">
          {data?.skills?.map(skill => (
            <div key={skill.area} data-testid={`skill-${skill.area}`} className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{skill.label}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${levelColor(skill.level)}`}>
                  {skill.level}
                </span>
              </div>
              <div className="h-2 bg-[#F0F2EB] rounded-full overflow-hidden mb-2">
                <div className="h-full bg-[#1A4331] transition-all duration-500" style={{ width: `${skill.score}%` }}></div>
              </div>
              <p className="text-xs text-[#5C665C]">{skill.score}% progress</p>
            </div>
          ))}
        </div>

        <div className="bg-[#8EAA97]/10 border border-[#8EAA97]/20 rounded-3xl p-6">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-[#1A4331] mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#1A4331] mb-2">How skills grow</h3>
              <p className="text-sm text-[#2D241E] leading-relaxed">
                Your skills improve as you record activity in Homegrown — planting crops, completing tasks, logging pests, recording harvests, and tracking soil tests. Keep growing and learning each week to level up.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Skills;
